// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// General illumination (M3-16).
//
// GI is the machine's ambient light: banks of bulbs under sections of the playfield that are
// on for the whole game and go out together for a blackout effect. It is the thing a player
// never notices until it changes, and then notices completely — a mode that kills the GI and
// leaves one insert lit is the single cheapest dramatic moment in pinball.
//
// **Not baked into the insert map, and not a light.** Two reasons, and they pull the same way:
//
//   * A GI zone is a *large soft area*, and the insert map holds one lamp per texel with hard
//     ownership. Painting a zone into it would overwrite every insert inside the zone, and
//     painting it as a separate channel would mean a hard rectangular edge across the middle
//     of the playfield, which is the one thing real GI never has.
//   * There are two of them on NEON NOVA and there will rarely be more than four. That is few
//     enough to be *uniforms*, evaluated per fragment with a soft falloff that costs nothing
//     and has no edge at all.
//
// So the zones are rectangles in texture space, handed to the playfield shader directly. No
// texture, no baking, no draw calls, and the softness is free because it is arithmetic rather
// than a picture of a gradient.

import { Vector3, Vector4 } from 'three'
import type { MachineDefinition, MachineLamp, MachineRegion } from '@core/machine'
import { chasePhases, lampBrightness } from './lampStates'
import { parseColour, type LampStateReader } from './lamps'
import { toTextureUv } from './lampShapes'

/**
 * Zones the shader can hold.
 *
 * A fixed bound because GLSL wants a constant loop bound, and four because no pinball machine
 * has ever needed a fifth: the vocabulary of GI is "upper", "lower", "left", "right", and
 * machines that want finer control use inserts, which is what inserts are for.
 */
export const MAX_GI_ZONES = 4

export interface BuiltGi {
  /** `uGiRect[i]` = (minU, minV, maxU, maxV) in playfield texture space. */
  readonly rects: Vector4[]
  /** `uGiColour[i]` = the zone's colour times its current brightness. */
  readonly colours: Vector3[]
  readonly count: number
  /** Zones that could not be built, each with the reason. */
  readonly skipped: readonly string[]
  /**
   * `dim` is 0…1 of *cut*, not of brightness.
   *
   * 05 §3's BIG BANG puts "playfield general illumination cut to black for 400 ms" behind the
   * white flash, and the cut has to happen here because this is what owns the illumination. It
   * is a parameter rather than a lamp state so that nothing in the rulesheet has to know: the
   * toy asks for the room to go dark and the room goes dark.
   */
  readonly update: (read: LampStateReader, step: number, dim?: number, reduced?: boolean) => void
}

/**
 * Build the GI zones a machine declares.
 *
 * A `gi` lamp names a region and the region says where it is — which is only possible because
 * the format now defines what a region *is*. Until it did, this field was a required string
 * that resolved to nothing, and general illumination could not be rendered at all.
 */
export function buildGi(definition: MachineDefinition): BuiltGi {
  const regions = new Map<string, MachineRegion>()
  for (const region of definition.regions ?? []) regions.set(region.id, region)

  const { width, length } = definition.dimensions.playfield
  const skipped: string[] = []
  const zones: MachineLamp[] = []

  for (const lamp of definition.lamps ?? []) {
    if (lamp.kind !== 'gi') continue
    if (lamp.region === undefined || !regions.has(lamp.region)) {
      // The validator refuses this, so reaching it means a machine got in another way.
      skipped.push(`${lamp.id}: names no region this machine declares`)
      continue
    }
    if (zones.length >= MAX_GI_ZONES) {
      skipped.push(`${lamp.id}: more than ${MAX_GI_ZONES} general-illumination zones`)
      continue
    }
    zones.push(lamp)
  }

  // Always the full array: a shader's uniform array has a fixed size, and leaving the unused
  // slots undefined is how a driver ends up reading whatever was in that register last.
  const rects: Vector4[] = []
  const colours: Vector3[] = []
  for (let i = 0; i < MAX_GI_ZONES; i++) {
    rects.push(new Vector4(0, 0, 0, 0))
    colours.push(new Vector3(0, 0, 0))
  }

  for (let i = 0; i < zones.length; i++) {
    const region = regions.get(zones[i]!.region!)!
    const min = toTextureUv(region.min, width, length)
    const max = toTextureUv(region.max, width, length)
    rects[i]!.set(Math.min(min[0], max[0]), Math.min(min[1], max[1]), Math.max(min[0], max[0]), Math.max(min[1], max[1]))
  }

  const rgb = zones.map((l) => parseColour(l.colour))
  const phases = chasePhases(groupsOf(zones))

  const update = (read: LampStateReader, step: number, dim = 0, reduced = false): void => {
    const kept = dim > 0 ? Math.max(0, 1 - dim) : 1
    for (let i = 0; i < zones.length; i++) {
      const lamp = zones[i]!
      const level = lampBrightness(read(lamp.id), step, phases.get(lamp.id) ?? 0, reduced) * kept
      const [r, g, b] = rgb[i]!
      colours[i]!.set((r / 255) * level, (g / 255) * level, (b / 255) * level)
    }
  }
  update(() => 0, 0)

  return { rects, colours, count: zones.length, skipped, update }
}

function groupsOf(lamps: readonly MachineLamp[]): Map<string, string[]> {
  const out = new Map<string, string[]>()
  for (const lamp of lamps) {
    if (lamp.group === undefined) continue
    const list = out.get(lamp.group)
    if (list) list.push(lamp.id)
    else out.set(lamp.group, [lamp.id])
  }
  return out
}

export const GI_UNIFORM_DECLARATIONS = `
  uniform vec4 uGiRect[ ${MAX_GI_ZONES} ];
  uniform vec3 uGiColour[ ${MAX_GI_ZONES} ];
  uniform float uGiCount;
  uniform float uGiGain;
  uniform float uGiFeather;
`

/**
 * The GI contribution at this fragment.
 *
 * A constant loop bound and a `step()` mask rather than a `break`, because a loop that exits
 * on a uniform is GLSL ES 3.00 and Three still compiles for 1.00 on a WebGL1 context. Four
 * iterations of arithmetic is cheaper than finding out which context a player has.
 *
 * The falloff is a `smoothstep` on the signed distance to the rectangle, so a zone has no
 * edge — which is the whole point. A hard rectangle of light across the middle of a playfield
 * looks like a rendering artefact; real GI is a glow with no boundary anyone can point at.
 *
 * **Weighted average, not a sum, and this is the part that has to be right.** NEON NOVA's two
 * zones meet across the middle of the playfield, and their soft edges overlap. Summing them
 * puts a bright band along the seam; taking the brighter of the two puts a *dark* one there,
 * because two curves both falling off have a dip where they cross. Dividing by the total
 * weight — floored at 1, so a fragment outside every zone still fades to nothing — makes the
 * seam a smooth blend from one zone's colour to the other's, which is what a real machine
 * with an amber lower bank and a violet upper one actually looks like.
 */
export const GI_FRAGMENT_HOOK = `
  vec3 giSum = vec3( 0.0 );
  float giWeight = 0.0;
  for ( int giIndex = 0; giIndex < ${MAX_GI_ZONES}; giIndex ++ ) {
    float giActive = step( float( giIndex ), uGiCount - 0.5 );
    vec4 giRect = uGiRect[ giIndex ];
    vec2 giOutside = max( giRect.xy - vMapUv, vMapUv - giRect.zw );
    float giDistance = max( giOutside.x, giOutside.y );
    float giFall = ( 1.0 - smoothstep( - uGiFeather, uGiFeather, giDistance ) ) * giActive;
    giSum += uGiColour[ giIndex ] * giFall;
    giWeight += giFall;
  }
  // Illumination is reflected by the ink. Adding the light colour on its own paints a
  // translucent grey/purple sheet over dark artwork instead of illuminating that artwork.
  totalEmissiveRadiance += diffuseColor.rgb * giSum / max( giWeight, 1.0 ) * uGiGain;
`
